import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getProfileCompletionPath,
  hasMissingProfile,
  isProfileCompletionPath,
} from "../../utils/onboarding";
import { useAuth } from "../../contexts/AuthContext";
import type { RequirementsStatus } from "./TermsModal";

interface RequirementsGateProps {
  missing: string[];
  status: RequirementsStatus;
  /** Публичные маршруты (логин, OAuth-callback) гейт не блокирует. */
  isPublicRoute: boolean;
  /** Ручной повтор после исчерпания авто-ретраев. */
  onRetry: () => void;
  children: React.ReactNode;
}

/**
 * Блокирует доступ к содержимому, пока не закрыты требования онбординга.
 *
 * Порядок обязателен и повторяет мобильный клиент:
 * 1. `terms` — блокирующий модальный экран (TermsModal). Пока он не принят,
 *    профиль не проверяется: иначе пользователь попал бы на заполнение
 *    профиля до согласия с условиями.
 * 2. `name` / `phone` — редирект на страницу заполнения профиля.
 *
 * Освобождение администраторов платформы от телефона применяется на сервере,
 * поэтому список `missing` уже не содержит `phone` для admin/super_admin.
 *
 * Гейт fail-closed: пока сервер не подтвердил требования (`pending`), контент
 * не показывается; при исчерпании авто-ретраев (`error`) показывается экран
 * ручного повтора. Раньше ошибка запроса молча открывала доступ — это
 * обходило и модалку условий, и редирект на профиль.
 */
const RequirementsGate: React.FC<RequirementsGateProps> = ({
  missing,
  status,
  isPublicRoute,
  onRetry,
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Не аутентифицирован — гейт не применяется (маршруты и так публичные).
  // Публичные маршруты (логин, OAuth-callback) исключены, чтобы блокировка не
  // помешала завершению обмена OAuth-кода на токен.
  if (!isAuthenticated || isPublicRoute) {
    return <>{children}</>;
  }

  // Требования ещё не подтверждены сервером — контент не показываем.
  if (status === "pending") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Все попытки запроса исчерпаны — даём пользователю возможность повторить.
  if (status === "error") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          minHeight: "40vh",
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">{t("requirements_check_failed_title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("requirements_check_failed_message")}
        </Typography>
        <Button variant="contained" onClick={onRetry}>
          {t("retry")}
        </Button>
      </Box>
    );
  }

  // Сначала условия: их показывает TermsModal, редирект на профиль здесь был бы
  // нарушением порядка шагов.
  if (missing.includes("terms")) {
    return <>{children}</>;
  }

  if (!hasMissingProfile(missing)) {
    return <>{children}</>;
  }

  // Пользователь уже на странице закрытия требований — редирект был бы петлёй.
  if (isProfileCompletionPath(pathname)) {
    return <>{children}</>;
  }

  return <Navigate to={getProfileCompletionPath()} replace />;
};

export default RequirementsGate;
